import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, MessageCircle, Copy, ArrowRight, ShoppingBag } from 'lucide-react';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { buildWhatsAppMessage, buildWhatsAppUrl } from '../lib/whatsapp';
import { formatCurrency } from '../lib/utils';
import { Restaurant, CartItem } from '../lib/types';
interface OrderState {
  restaurant: Restaurant;
  orderNumber: string;
  trackingToken: string;
  customerName: string;
  customerPhone: string;
  deliveryType: string;
  paymentMethod?: string;
  deliveryAddress?: string | null;
  mapsUrl?: string | null;
  notes?: string | null;
  ticketImageUrl?: string | null;
  items: CartItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
}
export function OrderConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as OrderState | null;
  const [copied, setCopied] = useState(false);
  if (!state) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-2288">
        <MarketplaceHeader data-id="element-2289" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-2290">
          <EmptyState icon={ShoppingBag} title="No hay pedido reciente" description="No encontramos información de un pedido reciente." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-2291" />
        </main>
        <Footer data-id="element-2292" />
      </div>;
  }
  const {
    restaurant,
    orderNumber,
    trackingToken,
    items,
    subtotal,
    deliveryFee,
    total
  } = state;
  const handleCopy = async () => {
    if (!restaurant.whatsapp_number) return;
    const message = buildWhatsAppMessage({
      restaurantName: restaurant.name,
      whatsappNumber: restaurant.whatsapp_number,
      currencySymbol: restaurant.currency_symbol,
      orderNumber: state.orderNumber,
      customerName: state.customerName,
      customerPhone: state.customerPhone,
      deliveryType: state.deliveryType,
      paymentMethod: state.paymentMethod,
      items: state.items,
      subtotal: state.subtotal,
      deliveryFee: state.deliveryFee,
      total: state.total,
      deliveryAddress: state.deliveryAddress,
      mapsUrl: state.mapsUrl,
      notes: state.notes,
      ticketImageUrl: state.ticketImageUrl,
      trackingToken: state.trackingToken
    });
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
  };
  const handleWhatsApp = () => {
    if (!restaurant.whatsapp_number) return;
    const message = buildWhatsAppMessage({
      restaurantName: restaurant.name,
      whatsappNumber: restaurant.whatsapp_number,
      currencySymbol: restaurant.currency_symbol,
      orderNumber: state.orderNumber,
      customerName: state.customerName,
      customerPhone: state.customerPhone,
      deliveryType: state.deliveryType,
      paymentMethod: state.paymentMethod,
      items: state.items,
      subtotal: state.subtotal,
      deliveryFee: state.deliveryFee,
      total: state.total,
      deliveryAddress: state.deliveryAddress,
      mapsUrl: state.mapsUrl,
      notes: state.notes,
      ticketImageUrl: state.ticketImageUrl,
      trackingToken: state.trackingToken
    });
    const url = buildWhatsAppUrl(restaurant.whatsapp_number, message);
    window.open(url, '_blank');
  };
  return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-2293">
      <MarketplaceHeader data-id="element-2294" />

      <main className="flex-1 flex items-center justify-center p-4 py-12" data-id="element-2295">
        <Card className="w-full max-w-lg p-8 text-center shadow-xl border-gray-100" data-id="element-2296">
          <div className="flex justify-center mb-6" data-id="element-2297">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center" data-id="element-2298">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" data-id="element-2299" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2300">
            Ya casi terminas 👇
          </h1>
          <p className="text-gray-600 mb-2" data-id="element-2301">
            Tu pedido{' '}
            <span className="font-bold text-gray-900" data-id="element-2302">#{orderNumber}</span> fue
            creado exitosamente.
          </p>
          <p className="text-sm text-gray-500 mb-8" data-id="element-2303">
            Tu pedido se enviará directamente al WhatsApp de{' '}
            <span className="font-semibold text-gray-700" data-id="element-2304">
              {restaurant.name}
            </span>
            .
          </p>

          <div className="bg-gray-50 rounded-2xl p-6 text-left mb-8 border border-gray-100" data-id="element-2305">
            <h3 className="font-bold text-gray-900 mb-4" data-id="element-2306">Resumen del Pedido</h3>
            <div className="space-y-4 mb-4 max-h-48 overflow-y-auto pr-2" data-id="element-2307">
              {items.map((item, idx) => {
              const optionsTotal = item.selected_options.reduce((sum, opt) => sum + opt.price_delta, 0);
              const lineTotal = (item.unit_price_snapshot + optionsTotal) * item.quantity;
              return <div key={idx} className="text-sm" data-id="element-2308">
                    <div className="flex justify-between" data-id="element-2309">
                      <span className="text-gray-900" data-id="element-2310">
                        <span className="font-semibold mr-1.5" data-id="element-2311">
                          {item.quantity}x
                        </span>
                        {item.item_name_snapshot}
                      </span>
                      <span className="font-medium text-gray-900 shrink-0 ml-3" data-id="element-2312">
                        {formatCurrency(lineTotal)}
                      </span>
                    </div>
                    {item.selected_options.length > 0 && <div className="mt-1 ml-6 space-y-0.5" data-id="element-2313">
                        {item.selected_options.map((opt, oi) => <p key={oi} className="text-xs text-gray-500" data-id="element-2314">
                            • {opt.option_name}
                            {opt.price_delta > 0 && <span className="text-gray-400" data-id="element-2315">
                                {' '}
                                +{formatCurrency(opt.price_delta)}
                              </span>}
                          </p>)}
                      </div>}
                    {item.notes && <p className="text-xs text-gray-400 mt-1 ml-6" data-id="element-2316">
                        📝 {item.notes}
                      </p>}
                  </div>;
            })}
            </div>
            <div className="border-t border-gray-200 pt-4 space-y-2 text-sm" data-id="element-2317">
              <div className="flex justify-between text-gray-600" data-id="element-2318">
                <span data-id="element-2319">Subtotal</span>
                <span data-id="element-2320">{formatCurrency(subtotal)}</span>
              </div>
              {deliveryFee > 0 && <div className="flex justify-between text-gray-600" data-id="element-2321">
                  <span data-id="element-2322">Envío</span>
                  <span data-id="element-2323">{formatCurrency(deliveryFee)}</span>
                </div>}
              <div className="flex justify-between font-bold text-gray-900 text-base pt-2" data-id="element-2324">
                <span data-id="element-2325">Total</span>
                <span data-id="element-2326">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4" data-id="element-2327">
            {restaurant.whatsapp_number && <>
                <Button size="lg" fullWidth onClick={handleWhatsApp} className="bg-[#25D366] hover:bg-[#20bd5a] text-white border-none shadow-lg shadow-emerald-200 h-14 text-lg" data-id="element-2328">
                  <MessageCircle className="w-6 h-6 mr-2" data-id="element-2329" />
                  Enviar pedido por WhatsApp
                </Button>

                <Button variant="secondary" size="lg" fullWidth onClick={handleCopy} className="h-14" data-id="element-2330">
                  <Copy className="w-5 h-5 mr-2" data-id="element-2331" />
                  {copied ? '¡Copiado!' : 'Copiar mensaje'}
                </Button>
              </>}

            <Button variant="ghost" size="lg" fullWidth onClick={() => navigate(`/track/${trackingToken}`)} className="h-14 border border-gray-200" data-id="element-2332">
              Seguir mi pedido
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-2333" />
            </Button>
          </div>
        </Card>
      </main>

      <Footer data-id="element-2334" />
    </div>;
}